import React from 'react';

import { useTranslations } from '@lang';
import Sticky from 'react-stickynode';
import { Link } from 'shared-routing';
import { BasketDetails } from 'shared-types/Basket';
import { Page } from 'shared-types/Common';

import { BasketDrawerItinerary } from '@common/components/BasketDrawer/BasketDrawerItinerary/BasketDrawerItinerary';
import { Button } from '@common/components/Button';
import { ItemListingRows } from '@common/components/ItemListingRows';
import PaymentMethods from '@common/components/PaymentMethods';
import { Price } from '@common/components/Price';
import { Spinner } from '@common/components/Spinner/Spinner';
import { Routes } from '@common/constants';
import PromoCodeForm from '@features/discounts/components/PromoCodeForm';
import { VoucherCodes } from '@features/discounts/components/VoucherCodeForm/VoucherCodes';
import { AppliedVoucherCodes } from '@features/discounts/components/VoucherCodeForm/AppliedVoucherCodes';
import { useGetSpaClassesPage } from '@features/spa/hooks/useGetSpaClassesPage';
import { useIsDesktop } from '@common/hooks/useIsDesktop';
import { isMobile, isTablet } from '@utils/grid';

import UpsellPrompts from '../UpsellPrompts';
import { BasketNotifications } from './BasketNotifications';
import { PromosTotals } from './PromosTotals';

import {
  ContinueShoppingRow,
  EmptyBasketContainer,
  EmptyBasketTitle,
  ItemsListingColumn,
  NotificationWrapper,
  Separator,
  SummaryColumn,
  SummaryContainer,
  SummarySection,
  SummaryTitle,
  Wrapper,
} from './ProductsListingForm.styles';
import { PriceWrapper } from './styles';

export type ProductsListingFormProps = {
  basketDetails: BasketDetails;
  handleBasketRecalculate: (...args: any) => void;
  handleSubmit: (...args: any) => void;
  isCheckoutLoading?: boolean;
  isFetching: boolean;
  tablesPage: Page;
  promoCodeFailure: boolean;
  tablesDisableChildBookings: boolean;
  propertyId: number;
  language: string;
  showItineraryView: boolean;
  isVouchersPaymentEnabled: boolean;
};

const ProductsListingForm: React.FC<ProductsListingFormProps> = ({
  basketDetails,
  handleBasketRecalculate,
  handleSubmit,
  isCheckoutLoading,
  isFetching,
  tablesPage,
  promoCodeFailure,
  tablesDisableChildBookings,
  propertyId,
  language,
  showItineraryView,
  isVouchersPaymentEnabled,
}) => {
  const { t } = useTranslations();

  const isDesktop = useIsDesktop();

  const { data: spaClassesPage } = useGetSpaClassesPage(propertyId, language);

  const { activePromo, discountTotals } = basketDetails;

  return (
    <NotificationWrapper>
      <BasketNotifications activePromo={activePromo} promoCodeFailure={promoCodeFailure} />
      <Wrapper>
        <Spinner showDelayMessage isActive={isFetching} />
        {basketDetails.items.length ? (
          <>
            <ItemsListingColumn>
              {showItineraryView ? (
                <BasketDrawerItinerary items={basketDetails.items} showAsGrid={isDesktop} />
              ) : (
                <ItemListingRows
                  onChange={handleBasketRecalculate}
                  items={basketDetails.items}
                  basketVouchers={basketDetails.vouchers}
                  isEditable
                  spaClassesPage={spaClassesPage ?? undefined}
                  tablesPage={tablesPage}
                  tablesDisableChildBookings={tablesDisableChildBookings}
                  propertyId={propertyId}
                />
              )}

              {isDesktop && <UpsellPrompts />}
            </ItemsListingColumn>
            <SummaryColumn id="summary-column">
              <Sticky enabled={isDesktop} top={60} bottomBoundary="#summary-column">
                <SummaryContainer>
                  {!!basketDetails.items.length && (
                    <>
                      <SummarySection>
                        <SummaryTitle>{t('basket.summary')}</SummaryTitle>
                        <Button
                          isPrimary
                          disabled={!basketDetails.items.length || isCheckoutLoading}
                          isLoading={isCheckoutLoading}
                          onClick={handleSubmit}
                        >
                          <span data-testid="clickCheckoutButton">{t('basket.checkout')}</span>
                        </Button>
                      </SummarySection>
                      <SummarySection>
                        <AppliedVoucherCodes />
                      </SummarySection>
                      <SummarySection>
                        <PromosTotals discountTotals={discountTotals} />
                        {!discountTotals ? (
                          <PriceWrapper>
                            {t('basket.total')}
                            <div>
                              <Price price={basketDetails.total} />
                            </div>
                          </PriceWrapper>
                        ) : null}
                      </SummarySection>
                      <SummarySection>
                        <PaymentMethods justifyContent="flex-end" />
                      </SummarySection>
                    </>
                  )}
                  <SummarySection>
                    <Separator />
                  </SummarySection>
                  <SummarySection>
                    <PromoCodeForm />
                  </SummarySection>
                  {isVouchersPaymentEnabled && (
                    <SummarySection>
                      <VoucherCodes />
                    </SummarySection>
                  )}
                </SummaryContainer>
              </Sticky>
              {(isMobile() || isTablet()) && <UpsellPrompts />}
            </SummaryColumn>
          </>
        ) : (
          <EmptyBasketContainer>
            <EmptyBasketTitle>{t('basket.emptyBasket')}</EmptyBasketTitle>
            {t('basket.haveNotFoundExperienceYet')}
            <ContinueShoppingRow>
              <Link route={Routes.HOME}>
                <Button isPrimary>{t('basket.continueShopping')}</Button>
              </Link>
            </ContinueShoppingRow>
          </EmptyBasketContainer>
        )}
      </Wrapper>
    </NotificationWrapper>
  );
};

export default ProductsListingForm;
